
























import { Component } from "vue-property-decorator";
import StockWarning from "@/components/designer/StockWarning.vue";
import { mixins } from "vue-class-component";
import * as option from "@/models/configurator/configurator_options";
import { FabricSwatch } from "@/models/products/fabric";
import { fabricTextureKey } from "@/helpers/fabric";
import { ribAttachment, grommetOnlyModels } from "@/mixins/business_logic/global/rib_rules";
import {
  BaseStore,
  LayoutStore,
  ShapeStore,
  CanopyStore,
  FrameStore,
} from "@/mixins/store";
import { ConfigCreator } from "@/mixins/configurator";
import { UmbrellaModel } from "@/models/products/umbrella";
import { ProductLineName } from "@/models/products/collection";
import { StockMixin } from "@/mixins/stock";
import { EventBus, PRICING_SUSPEND, PRICING_RESUME } from "@/event-bus";
@Component({
  components: {
    StockWarning,
  },
})
export default class Stock extends mixins(
  BaseStore,
  LayoutStore,
  ShapeStore,
  CanopyStore,
  FrameStore,
  ConfigCreator,
  StockMixin
) {
  protected showModal = false;
  protected ruleBProducts = [ProductLineName.Laurel, ProductLineName.Avalon]; // These productlines use set B of stock rules. The rest of the productlines are set A.
  protected ruleBModelExceptions = ["844FA03", "844FC03", "844FWB03 "]; // These umbrella models are in productlines that fall under rule set B, but are EXCEPTIONS and should have set A applied. There are no set A exceptions
  protected ribAttachment = ribAttachment;
  protected ProductLineName = ProductLineName;

  created() {
    this.$emit("isStock", this.isStockOnly);
  }

  get toggleImg(): string {
    let img;
    if (this.isStockOnly) {
      img = require("../../assets/images/toggle-true.png");
    } else {
      img = require("../../assets/images/toggle-false.png");
    }
    return img;
  }

  protected toggleStock(): void {
    if (!this.isStockOnly) {
      this.showModal = true;
    } else {
      this.$emit("isStock", !this.isStockOnly);
      this.addStock(false)
    }
  }

  /**
   * Currently two different sets of stock rules based on productline & a few exceptions based on model.
   */
  protected confirm(): void {
    this.addStock(true)
    this.$emit("isStock", true);
    this.showModal = false;
    const applyRuleA =
      !this.ruleBProducts.includes(this.collection.handle as ProductLineName) ||
      this.ruleBModelExceptions.includes(this.umbrellaModel!.model);
    this.resetUmbrellaToStock(applyRuleA ? "A" : "B");
  }

  protected close(): void {
    this.showModal = false;
  }

  /**
   *  RULE SET B --> Patio and beach: valance is stock, with the exception of 844FA03 and 844FC03 (Catalina), and 844FWB03 (Avalon).
   */
  protected async resetUmbrellaToStock(
    ruleSet: "A" | "B" = "A"
  ): Promise<void> {
    // The store mutations below land one at a time across many ticks. Pricing
    // watches canopy state and serializes its request body from live store
    // state at call time, so a watcher firing mid-chain would price a
    // half-applied config. Suspend pricing for the duration and let the resume
    // issue a single request against the finished stock config.
    EventBus.$emit(PRICING_SUSPEND);
    try {
      await this.applyStockConfig(ruleSet);
    } finally {
      EventBus.$emit(PRICING_RESUME);
    }
  }

  private async applyStockConfig(ruleSet: "A" | "B" = "A"): Promise<void> {
    let swatch = {} as FabricSwatch;
    if (this.mainFabric[0].grade === "a") {
      swatch = this.mainFabric[0];
    } else {
      swatch = await this.getFabricSwatch("R-172");
    }
    await this.addPreset(option.Preset.S1);
    await this.addMainCanopy(option.Panel.Solid);
    await this.addUndersideFabric(null);
    // Stock is preset S1 — single vent, so there is no middle vent to describe.
    // `ventMiddle` and `ventMiddleFabric` must move together (see the empty-fabric
    // branch of applyLayoutRules in layout_rules.ts): an empty ventMiddleFabric
    // requires a null ventMiddle. Writing Panel.Solid here alongside the empty
    // ventMiddleFabric set below left the pricing API reading ventMiddle as a
    // presence flag and pricing the umbrella as double-vented.
    await this.addVentMiddle(null);
    await this.addBindingFabric(null);
    if (this.umbrellaModel && grommetOnlyModels.includes(this.umbrellaModel.model)) {
      await this.addRib(option.Rib.Grommets);
    } else if (this.collection.handle === ProductLineName.Greenwich || this.collection.handle === ProductLineName.Eclipse) {
      await this.addRib(option.Rib.Grommets);
    } else if (ribAttachment.pockets.includes(this.collection.handle)) {
      await this.addRib(option.Rib.Pockets);
      await this.addRibFabric(swatch);
    } else {
      await this.addRib(null);
      await this.addRibFabric(null);
    }

    await this.addFinial(option.Finial.Ball);

    if (ruleSet === "A") {
      await this.addValance(option.Valance.Canopy);
    } else {
      await this.addValance(option.Valance.Standard);
      if (this.valanceFabric[0] && this.valanceFabric[0].grade === "a") {
        swatch = this.valanceFabric[0];
      } else {
        swatch = await this.getFabricSwatch("R-172");
      }
      await this.addValanceFabric([swatch]);
    }

    await this.addVentMiddleFabric([]);
    // Match the trim watcher's "to none" workflow: null out both trim slots
    // so the viewer's ChangeCanopyTrim(null, null) call from resetUmbrellaViewer
    // actually clears the trim mesh instead of repainting it with the canopy fabric.
    await this.addMainTrimFabric(null);
    await this.addMainTrimFabricInner(null);

    await this.addMainFabric([swatch]);
    // if (ribAttachment.pockets.includes(this.collection.handle)) {
    //   await this.addRibFabric(swatch);
    // } else {
    //   await this.addRibFabric(null);
    // }

    await this.addVentTopFabric([swatch]);
    // Stock layout is S1 (no vent trim) — null this out so the viewer's
    // ChangeVentTrim(null) call clears the mesh.
    await this.addVentTrimFabric(null);
    await this.resetUmbrellaViewer(ruleSet);
  }

  protected async resetUmbrellaViewer(ruleSet: "A" | "B" = "A"): Promise<void> {
    const currentModel: UmbrellaModel = this.umbrellaModel!;
    const data = await this.transformDataToConfigFile();
    // Viewer arguments come from the in-memory FabricSwatch objects (via
    // fabricTextureKey), NOT from `data.canopy.*` strings — those are the
    // SKU-payload form which may carry "::Collection" disambiguator suffixes
    // that the viewer doesn't understand.
    // ChangeCanopyLayout must run BEFORE ChangeCanopyFabric so the viewer
    // regroups mesh parts back to S1 (clearing any prior trim panel layout).
    // Mirrors the trim watcher's "to none" workflow: changeLayout → repaint.
    await this.$viewer.ChangeCanopyLayout(data.layout.preset);
    await this.$viewer.ChangeCanopyFabric(
      this.mainFabric[0].fabric_scale,
      fabricTextureKey(this.mainFabric[0])
    );
    if (currentModel.attributes && currentModel.attributes.vent) {
      await this.$viewer.ChangeVent("single");
      await this.$viewer.ChangeTopVentFabric(
        this.ventTopFabric[0].fabric_scale,
        fabricTextureKey(this.ventTopFabric[0])
      );
    }
    await this.$viewer.ChangeValance(data.canopy.valance);
    if (ruleSet === "B") {
      await this.$viewer.ChangeValanceFabric(
        this.valanceFabric[0].fabric_scale,
        fabricTextureKey(this.valanceFabric[0])
      );
    }
    await this.$viewer.ChangeUndersideFabric(null);
    await this.$viewer.ChangeBindingFabric(this.bindingFabric ? fabricTextureKey(this.bindingFabric) : null);
    await this.$viewer.ChangeRibAttachment(
      data.canopy.rib,
      data.canopy.rib === option.Rib.Grommets ? undefined : fabricTextureKey(this.mainFabric[0])
    );
    await this.$viewer.ChangeFinial(data.frame.finial as option.Finial);
    await this.$viewer.ChangeVentTrim(this.ventTrimFabric ? fabricTextureKey(this.ventTrimFabric) : null);
    await this.$viewer.ChangeCanopyTrim(
      this.mainTrimFabric ? fabricTextureKey(this.mainTrimFabric) : null,
      this.mainTrimFabricInner ? fabricTextureKey(this.mainTrimFabricInner) : null
    );
  }
}
